import React, { useState } from 'react';

import { FeatureCollection } from 'geojson';
import mapboxgl from 'mapbox-gl';
import Map, { MapRef, MarkerDragEvent, NavigationControl } from 'react-map-gl/mapbox';

import { PostGeoConfigLocation } from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import GeoMapPin from 'pages/Settings/GeoConfigSettings/GeoMap/GeoMapPin';
import ProfileDetail, {
  ProfileDetailProps,
} from 'pages/Settings/GeoConfigSettings/GeoMap/ProfileDetail';
import { useGeoConfigContext } from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import Skeleton from '@lotlinx-aws/sparky-component-library/Sparky/Skeleton';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

import styles from './GeoMap.module.scss';

export interface GeoMapProps extends ProfileDetailProps {
  mapRef: React.MutableRefObject<MapRef | undefined>;
  isLoading?: boolean;
  onEditing(location?: PostGeoConfigLocation): void;
}

//! BUG FIX: THIS IS REQUIRED TO GET THE MAP WORKER LOADER TO RUN ON DEV

// (mapboxgl as any).workerClass =
//   require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

/**
 * GeoMap
 */
export default function GeoMap({
  mapRef,
  defaultGeoConfig,
  onEditing,
  isLoading,
  ...otherProps
}: GeoMapProps) {
  const [movingPin, setMovingPin] = useState<PostGeoConfigLocation>();
  const [popupPin, setPopupPin] = useState<PostGeoConfigLocation>();
  const {
    creatingNewProfile,
    selectPin,
    editingPin,
    selectProfile,
    selectProfileIndex,
    updateTempGeoConfigPin,
  } = useGeoConfigContext();

  const { addToast } = useToast();

  const handleDragStart = (location: PostGeoConfigLocation) => setMovingPin(location);

  const handleDragEnd = (e: MarkerDragEvent, location: PostGeoConfigLocation) => {
    const { lat, lng } = e.target.getLngLat();
    updateTempGeoConfigPin({ ...location, latitude: lat, longitude: lng });
    onEditing({ ...location, latitude: lat, longitude: lng });
    setMovingPin(undefined);
  };

  const handleMouseEnter = (location: PostGeoConfigLocation) => {
    setPopupPin(location);
  };

  const handleMouseLeave = () => setPopupPin(undefined);

  const handleClick = () => {
    selectProfileIndex === 0 &&
      addToast({
        status: 'warning',
        description: `You can't make changes to your default radius profile. Create a new profile to customize your targeted areas.`,
      });
  };

  if (isLoading || defaultGeoConfig === undefined) {
    return <Skeleton height='100%' width='100%' className={styles.geoMap} />;
  }

  return (
    <div className={styles.geoMap}>
      <Map
        // @ts-ignore
        ref={mapRef}
        initialViewState={{
          latitude: defaultGeoConfig?.locations[0].latitude,
          longitude: defaultGeoConfig?.locations[0].longitude,
          zoom: 9,
        }}
        mapStyle='mapbox://styles/mapbox/light-v9'
        mapboxAccessToken={import.meta.env.VITE_APP_MAPBOX_API_TOKEN}
        // This is a style hack to make the map act responsively
        style={{
          position: 'relative',
          height: 'inherit',
          width: 'inherit',
          font: 'inherit',
        }}
        reuseMaps
      >
        <NavigationControl />

        {/*current unselecting profile pins*/}
        {selectProfile?.locations.map((location) => {
          return (
            !(selectPin?.id === location.id && selectPin?.name === location.name) && (
              <GeoMapPin
                key={`geo-map-pin-${location.id}-${location.name}-${location.latitude}-${location.longitude}`}
                location={location}
                isSelected={false}
                editing={false}
                moving={false}
                handleClick={handleClick}
                popupPin={popupPin}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
              />
            )
          );
        })}
        {/*current selecting profile pin*/}
        {selectPin && (
          <GeoMapPin
            key={`geo-map-pin-${selectPin?.id}-${selectPin.name}-${selectPin.latitude}-${selectPin.longitude}`}
            location={selectPin}
            isSelected={true}
            editing={editingPin?.id === selectPin.id && editingPin?.name === selectPin.name}
            moving={
              !!movingPin && movingPin?.id === selectPin.id && movingPin?.name === selectPin.name
            }
            popupPin={popupPin}
            handleClick={handleClick}
            handleDragStart={() => handleDragStart(selectPin)}
            handleDragEnd={(e) => handleDragEnd(e, selectPin)}
            handleMouseEnter={() => handleMouseEnter(selectPin)}
            handleMouseLeave={handleMouseLeave}
          />
        )}

        {editingPin && (
          <div className={styles.infoMessageContainer}>
            <Message
              category='info'
              heading='Interact with the map'
              children={
                <div className={styles.pointList}>
                  <Text className={styles.point}>
                    <span className={styles.bullet} />
                    To edit pin position, click pin to drag and move or edit in the settings panels
                  </Text>
                </div>
              }
            />
          </div>
        )}
      </Map>
      {!creatingNewProfile && (
        <ProfileDetail mapRef={mapRef} defaultGeoConfig={defaultGeoConfig} {...otherProps} />
      )}
    </div>
  );
}

export const createGeoJSONCircleKm = (
  center: [number, number],
  radiusInKm: number,
  points?: number,
) => {
  if (!points) {
    points = 64;
  }

  const [longitude, latitude] = center;
  const km = radiusInKm;
  const coordinates = [];
  const distanceX = km / (111.32 * Math.cos((latitude * Math.PI) / 180));
  const distanceY = km / 110.574;

  for (let i = 0; i < points; ++i) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    coordinates.push([longitude + x, latitude + y]);
  }
  coordinates.push(coordinates[0]);

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      },
    ],
  } as FeatureCollection;
};

export const createGeoJSONCircleMiles = (
  center: [number, number],
  radiusInMiles: number,
  points?: number,
) => {
  const radiusInKm = radiusInMiles * 1.609344;
  return createGeoJSONCircleKm(center, radiusInKm, points);
};
